/**
 * ``captureEvent()`` — the single sanctioned entry point for every
 * frontend PostHog event.
 *
 * Compile-time guarantee: the ``event`` argument is typed against the
 * discriminated union in ``./registry.ts``. Using an unregistered
 * ``event_name`` or omitting a required property is a TypeScript
 * error before commit.
 *
 * Runtime guarantees (in order, per Plan_05 § "Enforcement points"):
 *   1. SDK gates: silently no-op if posthog-js is not loaded or the
 *      session is opted-out (Global Admins, kill-switch users).
 *   2. Event-name shape: must match ``EVENT_NAME_PATTERN`` (3
 *      dot-separated lowercase segments, second + third start with a
 *      letter, may contain underscores).
 *   3. Verb whitelist: segment-3 must be in ``ALLOWED_VERBS``.
 *      Catches present-tense / imperative slips that the regex can't.
 *   4. Zod schema: full payload validated against the per-event
 *      schema. In dev, schema failure hard-blocks the capture. In
 *      prod, the failure is logged but the event still ships
 *      (decision #11 / PR-4 schema-drift call): a stale tab with an
 *      older property name must not poison the analytics pipeline.
 *   5. Forbidden-property fence: every key on the outgoing payload
 *      runs through ``isForbiddenProperty``. Matches are dropped with
 *      a warning. Belt-and-braces against the schemas — Zod alone
 *      should already prevent these, but this is the second wall.
 *
 * Never raises. Capture is best-effort telemetry; a logging hiccup
 * must not crash a user flow.
 */

import posthog from "posthog-js";

import { isForbiddenProperty } from "./governance/forbidden";
import { isAllowedVerb } from "./governance/verbs";
import { EventSchema, type Event } from "./registry";

export const EVENT_NAME_PATTERN = /^[a-z]+\.[a-z][a-z_]*\.[a-z][a-z_]*$/;

function warn(message: string, detail?: unknown): void {
  console.warn(`[analytics] ${message}`, detail ?? "");
}

function isDebugMode(): boolean {
  return process.env.NEXT_PUBLIC_POSTHOG_DEBUG === "true";
}

function debugLog(message: string, detail?: unknown): void {
  if (!isDebugMode()) return;
  console.info(`[analytics] ${message}`, detail ?? "");
}

function isSdkReady(): boolean {
  // posthog-js exposes ``__loaded`` only after ``init()`` resolves.
  // The flag is missing on the type declarations; cast through unknown
  // to avoid an ``any``.
  const flag = (posthog as unknown as { __loaded?: boolean }).__loaded;
  return flag === true;
}

export function captureEvent<E extends Event>(event: E): void {
  if (!isSdkReady()) {
    debugLog(`skipped (SDK not ready): ${event.event_name}`);
    return;
  }
  if (posthog.has_opted_out_capturing()) {
    debugLog(`skipped (opted out): ${event.event_name}`);
    return;
  }

  // Event-name shape — fast-reject before touching Zod.
  if (!EVENT_NAME_PATTERN.test(event.event_name)) {
    warn(`invalid event_name: ${event.event_name}`);
    return;
  }

  // Verb whitelist — past-tense check the regex can't perform.
  // Segment 3 may be compound (``row_clicked``, ``period_changed``,
  // ``reset_requested``); the past-tense verb is always the trailing
  // word. Splitting on ``_`` and checking the last token matches the
  // catalog convention.
  const segments = event.event_name.split(".");
  const lastSegment = segments[segments.length - 1] ?? "";
  const verb = lastSegment.split("_").pop() ?? "";
  if (!isAllowedVerb(verb)) {
    warn(
      `unrecognised verb (not past-tense?): "${verb}" in ${event.event_name}`,
    );
    return;
  }

  // Full Zod validation. In dev, hard-block on failure so the author
  // sees the mismatch in their console immediately. In prod, log and
  // proceed — the stale-tab scenario must not silently drop data.
  const validated = EventSchema.safeParse(event);
  if (!validated.success) {
    warn("schema validation failed", {
      event_name: event.event_name,
      errors: validated.error.flatten(),
    });
    if (process.env.NODE_ENV !== "production") return;
  }

  // Merge the original event keys with Zod's parsed output. Zod's
  // default ZodObject strips unknown keys silently — if we only used
  // ``validated.data`` here, an extra forbidden key like ``email``
  // would be removed by Zod before the forbidden-property fence ever
  // sees it, and a stale tab sending a new (innocent) property in
  // prod-drift mode would have it silently dropped. Merging in the
  // original event preserves those keys; ``validated.data`` overlays
  // the transformed values (uppercase ticker, uppercase country, etc.)
  // for fields the schema does know about. When validation failed we
  // fall back to the raw event entirely.
  const payload: Record<string, unknown> = validated.success
    ? {
        ...(event as unknown as Record<string, unknown>),
        ...(validated.data as Record<string, unknown>),
      }
    : (event as unknown as Record<string, unknown>);

  // Strip forbidden keys defensively. Schemas already prevent these
  // for declared properties, but a stale tab or a future regression
  // could still try to attach them — drop with a warning.
  const cleaned: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(payload)) {
    if (key === "event_name") continue;
    if (isForbiddenProperty(key)) {
      warn(
        `dropped forbidden property "${key}" from event ${event.event_name}`,
      );
      continue;
    }
    if (value === undefined || value === null) continue;
    cleaned[key] = value;
  }

  try {
    posthog.capture(event.event_name, cleaned);
    debugLog(`captured: ${event.event_name}`, {
      keys: Object.keys(cleaned),
    });
  } catch (err) {
    // posthog-js routes its own errors through the SDK's ``on_xhr_error``
    // hook; capture() itself shouldn't throw. Belt-and-braces for the
    // rare future regression.
    warn("posthog.capture raised — swallowed", err);
  }
}
